import React from 'react';
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Code, Zap, Users, Award } from "lucide-react";

const About: React.FC = () => {
  const features = [
    {
      icon: <Code className="w-8 h-8 text-purple-400" />,
      title: "AI-Powered Development",
      description: "Leveraging cutting-edge AI tools and techniques to create efficient, scalable solutions that push the boundaries of modern web development."
    },
    {
      icon: <Zap className="w-8 h-8 text-cyan-400" />,
      title: "Performance Focused",
      description: "Obsessed with creating lightning-fast applications that deliver exceptional user experiences through optimized code and best practices."
    },
    {
      icon: <Users className="w-8 h-8 text-pink-400" />,
      title: "Community Builder",
      description: "Passionate about fostering developer communities, sharing knowledge, and helping others grow their skills in the ever-evolving tech landscape."
    },
    {
      icon: <Award className="w-8 h-8 text-purple-400" />,
      title: "Industry Expertise",
      description: "Years of experience working with Fortune 500 companies and startups, delivering production-ready solutions that scale."
    }
  ];

  const stats = [
    { number: "500+", label: "Projects Completed" },
    { number: "50K+", label: "Lines of Code" },
    { number: "100+", label: "Happy Clients" },
    { number: "5+", label: "Years Experience" }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            About <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">VibeCode</span>
          </h2>
          <p className="text-xl text-gray-300 leading-relaxed">
            Transforming ideas into powerful digital experiences through innovative development, 
            AI integration, and a passion for creating tools that empower the developer community.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-6 mb-16 max-w-4xl mx-auto">
          {stats.map((stat, index) => (
            <div key={index} className="text-center p-6 rounded-lg bg-gray-800/50 border border-gray-700/50">
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                {stat.number}
              </div>
              <div className="text-gray-400 text-sm md:text-base">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Features Grid */}
        <div className="space-y-8 max-w-4xl mx-auto">
          {features.map((feature, index) => (
            <Card key={index} className="bg-gray-800/50 border-gray-700/50 hover:border-purple-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/10">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                  <div className="flex-shrink-0 p-3 rounded-lg bg-gray-700/50">
                    {feature.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl md:text-2xl font-semibold text-white mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Mission Statement */}
        <div className="mt-16 max-w-4xl mx-auto">
          <Card className="bg-gradient-to-r from-purple-900/30 to-cyan-900/30 border-purple-500/30">
            <CardContent className="p-8 md:p-12 text-center">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Our Mission
              </h3>
              <p className="text-lg text-gray-300 leading-relaxed">
                To democratize access to cutting-edge development tools and knowledge, 
                empowering developers worldwide to build the next generation of digital experiences. 
                We believe in the power of community, continuous learning, and the transformative 
                potential of AI-assisted development.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default About;